import { useCallback, useEffect, useMemo, useState } from 'react'
import { Plus, Search, Settings2, Ticket } from 'lucide-react'
import { Link } from '../router.js'
import { usePermissions } from '../admin/primitives.js'
import { SegmentedControl } from '../admin/pickers.js'
import { Checkbox } from '../admin/form.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Trail } from '../navigation/Trail.js'
import { businessNightDayISO } from '../soirees/business-night.js'
import { remplacerRecherche } from '../finance/adresse-ecran.js'
import { chargerListe } from './soirees-maison-api.js'
import { useLecture, usePreferencesBilletterie } from './soirees-maison-hooks.js'
import { AssistantSoiree, LienNouvelleSoiree, useAssistantSoiree } from './AssistantSoireePage.js'
import { VignetteAffiche } from './AfficheSoiree.js'
import { Jauge } from './FicheVentes.js'
import {
  NOM_BILLETTERIE, STATUT_LIBELLE, compteARebours, filtresListeDepuisUrl, filtresListeVersUrl, instantParis, nuitCourte, part, phraseTronque, raisonEcriture,
  requeteListe, sectionsDeLaListe, suiteErreurAcces, tonStatut, venteSurJauge,
  type FiltresListe, type Periode,
} from './soirees-maison-model.js'

/**
 * /soirees/billetterie — LES SOIRÉES DE BILLETTERIE LIVE CLUB (22/10/2026).
 *
 * La liste vient de la billetterie, propriétaire des soirées (contrat
 * `soirees-v1` §4.2) ; Gestion n'en garde aucune copie. Les soirées
 * d'entraînement sont masquées par défaut (réglage « entraînement visible ») :
 * on peut les montrer ici, le temps de la visite. Une liste plus longue que la
 * limite le dit, au lieu de laisser croire qu'elle est complète.
 *
 * Chaque soirée porte la vignette 16:9 de son affiche (§9.6), chargée
 * seulement quand elle approche de l'écran. « Nouvelle soirée » ouvre
 * l'assistant par-dessus la liste, qui se relit à la création.
 *
 * RANGÉE COMME LA LISTE DES ÉVÉNEMENTS CHEZ SHOTGUN (23/10/2026) : une
 * colonne au centre, une recherche, des sections par état (« Ce soir », en
 * vente, programmées, brouillons…), et pour chaque soirée son compte à
 * rebours (« J-6 »), sa vignette, ses ventes sur la jauge et la barre de
 * remplissage.
 */

const CHEMIN = '/soirees/billetterie'

const PERIODES: { value: Periode; label: string }[] = [
  { value: 'a-venir', label: 'À venir' },
  { value: 'passees', label: 'Passées' },
  { value: 'toutes', label: 'Toutes' },
]

export function SoireesMaisonPage() {
  const { prefs } = usePreferencesBilletterie()
  // Les filtres sont dans l'adresse (entrée d'historique remplacée, jamais empilée).
  const [filtres, setFiltres] = useState<FiltresListe>(() => filtresListeDepuisUrl(new URLSearchParams(window.location.search)))
  useEffect(() => { remplacerRecherche(CHEMIN, filtresListeVersUrl(filtres)) }, [filtres])
  const periode = filtres.periode
  const setPeriode = (p: Periode) => setFiltres((f) => ({ ...f, periode: p }))
  // `null` : on suit le réglage de Gestion ; un clic le remplace pour cette visite.
  const entrainement = filtres.entrainement
  const setEntrainement = (e: boolean) => setFiltres((f) => ({ ...f, entrainement: e }))
  const avecEntrainement = entrainement ?? prefs.entrainement_visible
  const q = useMemo(() => requeteListe({ periode, entrainement: avecEntrainement, nuitDuJour: businessNightDayISO(Date.now()), limite: 200 }),
    [periode, avecEntrainement])
  const charger = useCallback(() => chargerListe(q), [q])
  const { lecture, recharger } = useLecture(charger, JSON.stringify(q), prefs.rafraichissement_s)
  const assistant = useAssistantSoiree()
  const data = lecture.etat === 'ok' ? lecture.data : null
  // La recherche est une loupe sur la liste lue : elle ne part pas dans l'adresse ni chez la billetterie.
  const [recherche, setRecherche] = useState('')
  const nuitDuJour = businessNightDayISO(Date.now())
  const sections = data ? sectionsDeLaListe(data.soirees, nuitDuJour, recherche) : []
  const ecriture = data ? raisonEcriture(data.ecriture) : null
  // Le droit se lit dans la session : une billetterie arrêtée ou non raccordée ne rend pas `_actions`, et le bouton ne disparaît pas sans rien dire.
  const { permissions } = usePermissions()
  const prepare = permissions.includes('ticketing.event.prepare') && (data ? data.actions.preparer : lecture.etat === 'erreur')
  const raisonPreparer = lecture.etat === 'erreur' ? lecture.erreur.message : ecriture

  return (
    <main className="lc-page lc-page--management lc-stagger bs-page bs-page--liste">
      <SmartBack fallback="/soirees" />
      <Trail />
      <div className="bs-entete">
        <div className="bs-entete__titre">
          <h1 className="lc-title bs-titre-icone"><Ticket size={26} aria-hidden="true" /> {NOM_BILLETTERIE}</h1>
          <p className="lc-subtitle">
            Les soirées de la billetterie du club : préparées ici, vendues à la porte. Pendant la démo, elles ne comptent nulle part — ni calendrier, ni compta, ni Accueil.
          </p>
        </div>
        <div className="bs-entete__actions">
          {data?.actions.regler ? <Link href="/settings/billetterie" className="lc-btn lc-btn--ghost"><Settings2 size={15} aria-hidden="true" /> Réglages</Link> : null}
          {prepare ? (
            raisonPreparer
              ? <button type="button" className="lc-btn" disabled title={raisonPreparer}><Plus size={15} aria-hidden="true" /> Nouvelle soirée</button>
              : <LienNouvelleSoiree ouvrir={assistant.ouvrir} />
          ) : null}
        </div>
      </div>

      <label className="bs-recherche">
        <Search size={16} aria-hidden="true" />
        <input type="search" className="bs-recherche__champ" placeholder="Rechercher une soirée" aria-label="Rechercher une soirée"
          value={recherche} onChange={(e) => setRecherche(e.target.value)} />
      </label>
      <div className="bs-filtres">
        <SegmentedControl value={periode} options={PERIODES} onChange={setPeriode} ariaLabel="Période affichée" />
        <Checkbox checked={avecEntrainement} onChange={(v) => setEntrainement(v)} label="Montrer les soirées d’entraînement" />
      </div>

      {data ? <p className="lc-hint bs-demo">{data.demo.message}</p> : null}
      {ecriture && data?.actions.preparer ? <p className="bs-note" role="status">{ecriture}</p> : null}

      <section aria-label="Soirées" className="bs-section">
        {lecture.etat === 'chargement' ? <div className="so-skel" aria-busy="true"><span /><span /><span /></div> : null}
        {lecture.etat === 'erreur' ? (
          <div className={`bs-note${suiteErreurAcces(lecture.erreur) ? ' bs-note--ko' : ' bs-note--doute'}`} role="status">
            <span>{lecture.erreur.message}</span>
            {suiteErreurAcces(lecture.erreur) ? <span className="lc-hint">{suiteErreurAcces(lecture.erreur)}</span> : null}
          </div>
        ) : null}
        {data ? (
          data.soirees.length === 0 ? (
            <p className="gd-calm">
              {periode === 'a-venir' ? `Aucune soirée à venir dans ${NOM_BILLETTERIE}.` : periode === 'passees' ? 'Aucune soirée passée.' : 'Aucune soirée.'}
              {!avecEntrainement ? ' Les soirées d’entraînement sont masquées.' : ''}
            </p>
          ) : (
            sections.length === 0 ? <p className="gd-calm">Aucune soirée ne contient « {recherche.trim()} ».</p> : sections.map((sec) => (
              <section key={sec.titre} className="bs-liste-section" aria-label={sec.titre}>
                <h2 className="bs-h2">{sec.titre} <span className="bs-h2__compte">· {sec.soirees.length}</span></h2>
                <ul className="bs-liste">
                  {sec.soirees.map((s) => {
                    const rebours = compteARebours(s.nuit, nuitDuJour)
                    const pct = s.jauge_totale ? Math.round(part(s.vendus, s.jauge_totale) * 100) : null
                    return (
                      <li key={s.id} className="bs-liste__ligne">
                        <span className={`bs-rebours-tuile${rebours.proche ? ' bs-rebours-tuile--proche' : ''}`} aria-label={`Nuit ${nuitCourte(s.nuit)} : ${rebours.texte}`}>{rebours.texte}</span>
                        <Link href={`/soirees/billetterie/${s.id}`} className="bs-carte">
                          <VignetteAffiche soireeId={s.id} affiche={s.affiche} />
                          <span className="bs-carte__nom">
                            <strong>{s.nom}</strong>
                            <span className="lc-hint">
                              {nuitCourte(s.nuit)}{s.ouverture_heure && s.fermeture_heure ? ` · ${s.ouverture_heure} → ${s.fermeture_heure}` : ''}
                              {s.statut === 'PROGRAMMEE' && s.mise_en_vente_a ? ` · en vente le ${instantParis(s.mise_en_vente_a)}` : ''}
                            </span>
                          </span>
                          <span className="bs-carte__etat">
                            <span className={`lc-pill lc-pill--${tonStatut(s.statut)}`}><span className={`lc-dot${tonStatut(s.statut) === 'idle' ? '' : ` lc-dot--${tonStatut(s.statut)}`}`} aria-hidden="true" />{STATUT_LIBELLE[s.statut]}</span>
                            {s.entrainement ? <span className="lc-pill lc-pill--idle">Entraînement</span> : null}
                          </span>
                          <span className="bs-carte__chiffres">
                            <span><strong>{venteSurJauge(s.vendus, s.jauge_totale)}</strong> vendus</span>
                            <span className="lc-hint">{s.entres.toLocaleString('fr-FR')} entrés</span>
                          </span>
                          <span className="bs-carte__jauge">
                            {pct !== null ? <><Jauge valeur={s.vendus} max={s.jauge_totale ?? 0} libelle={`Remplissage de ${s.nom}`} fine /><strong>{pct} %</strong></> : <span className="lc-hint">—</span>}
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </section>
            ))
          )
        ) : null}
        {data?.tronque ? <p className="lc-hint">{phraseTronque(periode, data.soirees.length)}</p> : null}
        {lecture.etat === 'ok' && lecture.erreur ? <p className="lc-hint" role="status">Dernière relecture en échec : {lecture.erreur.message}</p> : null}
      </section>
      {/* Monté selon le DROIT seul : une relecture de la liste ne doit jamais emporter la fenêtre ouverte. */}
      {permissions.includes('ticketing.event.prepare') ? <AssistantSoiree ouvert={assistant.ouvert} tour={assistant.tour} onFermer={assistant.fermer} onCreee={recharger} /> : null}
    </main>
  )
}
